import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { usePackageInclusions } from "@/hooks/usePackageInclusions";
import { usePackageExclusions } from "@/hooks/usePackageExclusions";
import { PackageData } from "@/pages/CreatePackage";
import { PackageItem } from "@/types/package-item";
import { ArrowLeft, ArrowRight, CheckCircle, PlusCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PackageInclusionDialog } from "@/components/package-inclusions/PackageInclusionDialog";
import { PackageExclusionDialog } from "@/components/package-exclusions/PackageExclusionDialog";
import { ProgressSteps } from "./ProgressSteps";
interface InclusionsStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const InclusionsStep = ({
  data,
  onUpdate,
  onNext,
  onPrev,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: InclusionsStepProps) => {
  const [isInclDialogOpen, setInclDialogOpen] = useState(false);
  const [isExclDialogOpen, setExclDialogOpen] = useState(false);
  const {
    inclusions: allInclusions,
    isLoading: isLoadingInclusions,
    addInclusion,
    isAdding: isAddingInclusion
  } = usePackageInclusions();
  const {
    exclusions: allExclusions,
    isLoading: isLoadingExclusions,
    addExclusion,
    isAdding: isAddingExclusion
  } = usePackageExclusions();
  const handleSaveInclusion = async (values: {
    text_ar: string;
    text_en: string;
  }) => {
    try {
      await addInclusion(values);
      toast.success("تمت إضافة البند بنجاح.");
      setInclDialogOpen(false);
    } catch (error) {
      toast.error("حدث خطأ أثناء إضافة البند.");
      console.error(error);
    }
  };
  const handleSaveExclusion = async (values: {
    text_ar: string;
    text_en: string;
  }) => {
    try {
      await addExclusion(values);
      toast.success("تمت إضافة البند بنجاح.");
      setExclDialogOpen(false);
    } catch (error) {
      toast.error("حدث خطأ أثناء إضافة البند.");
      console.error(error);
    }
  };
  const handleInclusionChange = (item: PackageItem, checked: boolean) => {
    const currentInclusions = data.inclusions || [];
    const newInclusions = checked ? [...currentInclusions, item] : currentInclusions.filter(i => i.id !== item.id);
    onUpdate({
      inclusions: newInclusions
    });
  };
  const handleExclusionChange = (item: PackageItem, checked: boolean) => {
    const currentExclusions = data.exclusions || [];
    const newExclusions = checked ? [...currentExclusions, item] : currentExclusions.filter(e => e.id !== item.id);
    onUpdate({
      exclusions: newExclusions
    });
  };
  const isIncluded = (item: PackageItem) => {
    return !!data.inclusions?.find(i => i.id === item.id);
  };
  const isExcluded = (item: PackageItem) => {
    return !!data.exclusions?.find(e => e.id === item.id);
  };
  const renderItems = (items: PackageItem[] | undefined, isLoading: boolean, handler: (item: PackageItem, checked: boolean) => void, checker: (item: PackageItem) => boolean) => {
    if (isLoading) {
      return Array.from({
        length: 3
      }).map((_, i) => <div key={i} className="flex items-center space-x-2 space-x-reverse my-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-[200px]" />
        </div>);
    }
    if (!items || items.length === 0) {
      return <p className="text-sm text-gray-500">لا توجد بنود متاحة.</p>;
    }
    return items.map(item => <div key={item.id} className="flex items-center space-x-2 space-x-reverse">
        <Checkbox id={`${item.id}-${handler.name}`} checked={checker(item)} onCheckedChange={checked => handler(item, !!checked)} />
        <Label htmlFor={`${item.id}-${handler.name}`} className="font-normal cursor-pointer">
          {item.text_ar}
        </Label>
      </div>);
  };
  return <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            شمول
          </CardTitle>
          
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-lg">البرنامج يشمل</h3>
                <Button size="sm" variant="outline" onClick={() => setInclDialogOpen(true)}>
                  <PlusCircle className="ml-2 h-4 w-4" />
                  إضافة بند
                </Button>
              </div>
              <div className="space-y-3">
                {renderItems(allInclusions, isLoadingInclusions, handleInclusionChange, isIncluded)}
              </div>
            </div>
            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-lg">البرنامج لا يشمل</h3>
                <Button size="sm" variant="outline" onClick={() => setExclDialogOpen(true)}>
                  <PlusCircle className="ml-2 h-4 w-4" />
                  إضافة بند
                </Button>
              </div>
              <div className="space-y-3">
                {renderItems(allExclusions, isLoadingExclusions, handleExclusionChange, isExcluded)}
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t mt-6">
            <Button variant="outline" onClick={onPrev}>
              <ArrowRight className="w-4 h-4 ml-2" />
              السابق
            </Button>
            <Button onClick={onNext} className="bg-blue-600 hover:bg-blue-700">
              التالي
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <PackageInclusionDialog open={isInclDialogOpen} onOpenChange={setInclDialogOpen} onSave={handleSaveInclusion} isSaving={isAddingInclusion} />
      <PackageExclusionDialog open={isExclDialogOpen} onOpenChange={setExclDialogOpen} onSave={handleSaveExclusion} isSaving={isAddingExclusion} />
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={6} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </>;
};